function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import moment from "moment";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import Auth from "../../functions/authenticate";
import { INPUT, LABEL } from "../../components/form-styles";
import { PlunkDropDown } from "../../components/analytics-kit";
import { hasTenantFeature } from "../../functions/tenant-features";
import { NotEnabled } from "./common";
import { api, errText, isNotEnabled, isAdmin, idStr, num, PROVIDER_LABEL } from "./api";

/*
 * Outreach mailboxes (2026-10-09 redesign, Mailboxes.dc mockup). Same API as before:
 *
 *   GET infrastructure (domains + DNS flags, mailbox health rows, warmup pool) + GET mailboxes (display name, token).
 *   Domain ··· : Records = GET domains/{id}/dns, Check again = POST domains/{id}/recheck, Pause / Resume,
 *                Retire (Administrator, confirm). Add a domain = POST domains {domain, mailProvider, buy:false}.
 *   Mailbox ···: Start warming up (POST mailboxes/{id}/resume), Pause / Resume, Edit (PUT caps + display name +
 *                signature), Turn off (Administrator, confirm; POST disable deletes the stored token).
 *                Add a mailbox = POST mailboxes. Connect a mailbox you have = POST mailboxes/connect/{provider}/start.
 *
 * Warm-up ramp (WarmupRamp.cs): no new outreach email until day 8, full pace from day 22 ("day X of 21").
 * Health pause threshold (MailboxHealthService HealthThresholds.BounceRate): 7-day bounce ≥ 3 %. The API does not
 * return it, so it is mirrored here.
 */

const lbl = {
  ...LABEL,
  margin: "8px 0 4px"
};
const WARMUP_DAYS = 21;
const BOUNCE_PAUSE_PCT = 3;

/** A small "···" menu; outside clicks close it (ref containment, not stopPropagation). */
class Menu extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "away", e => {
      if (this.ref.current && !this.ref.current.contains(e.target)) this.close();
    });
    _defineProperty(this, "toggle", () => {
      const open = !this.state.open;
      this.setState({
        open
      });
      if (open) document.addEventListener("mousedown", this.away);else document.removeEventListener("mousedown", this.away);
    });
    _defineProperty(this, "close", () => {
      this.setState({
        open: false
      });
      document.removeEventListener("mousedown", this.away);
    });
    this.state = {
      open: false
    };
    this.ref = /*#__PURE__*/React.createRef();
  }
  componentWillUnmount() {
    document.removeEventListener("mousedown", this.away);
  }
  render() {
    const {
      items,
      label = "More actions",
      disabled,
      trigger,
      triggerCls = "ori-btn ori-more"
    } = this.props;
    const list = (items || []).filter(Boolean);
    if (!list.length) return null;
    return /*#__PURE__*/React.createElement("span", {
      className: "ori-menu",
      ref: this.ref
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: triggerCls,
      "aria-label": label,
      title: trigger ? undefined : label,
      disabled: disabled,
      onClick: this.toggle
    }, trigger || "···"), this.state.open ? /*#__PURE__*/React.createElement("div", {
      className: "ori-menu-pop",
      role: "menu"
    }, list.map((it, i) => /*#__PURE__*/React.createElement("button", {
      key: i,
      type: "button",
      role: "menuitem",
      className: it.danger ? "danger" : "",
      disabled: it.disabled,
      onClick: () => {
        this.close();
        it.onClick();
      }
    }, it.label))) : null);
  }
}

/** A mailbox's state in plain words. */
function mailboxState(h) {
  const reason = String(h.pausedReason || "");
  switch (h.status) {
    case "provisioning":
      return {
        text: "Not started",
        tone: "plain"
      };
    case "warming":
      return {
        text: `Warming up, day ${num(h.warmDay)}`,
        tone: "warn"
      };
    case "active":
      return {
        text: h.throttled ? "Ready, slowed down by the provider" : "Ready",
        tone: h.throttled ? "warn" : "good"
      };
    case "disabled":
      return {
        text: "Turned off",
        tone: "plain"
      };
    case "paused":
      {
        if (reason.indexOf("auto:") === 0) {
          if (/bounce/i.test(reason)) return {
            text: "Paused: too many bounces",
            tone: "bad"
          };
          if (/failure/i.test(reason)) return {
            text: "Paused: sending errors",
            tone: "bad"
          };
          if (/rescue|spam/i.test(reason)) return {
            text: "Paused: landing in spam",
            tone: "bad"
          };
          if (/throttl/i.test(reason)) return {
            text: "Paused: the provider slowed it down",
            tone: "bad"
          };
          return {
            text: "Paused automatically",
            tone: "bad"
          };
        }
        return {
          text: "Paused",
          tone: "warn"
        };
      }
    default:
      return {
        text: h.status || "Unknown",
        tone: "plain"
      };
  }
}

/** Health findings in plain words (the warm-up day finding is already the state line). */
function plainFinding(f) {
  const s = String(f || "");
  if (/^warming, day/i.test(s)) return null;
  if (/bounce/i.test(s)) return "Too many emails are bouncing.";
  if (/send failures/i.test(s)) return "Too many emails failed to send.";
  if (/spam rescues/i.test(s)) return "Some warm-up emails are landing in spam.";
  if (/throttl/i.test(s)) return "The provider slowed this mailbox down in the last few hours.";
  return s;
}

/** A domain's setup state in plain words. */
function domainState(d) {
  if (d.status === "retired") return {
    text: "Retired",
    tone: "plain"
  };
  if (d.status === "paused") return {
    text: "Paused",
    tone: "warn"
  };
  const missing = [["SPF", d.spf], ["DKIM", d.dkim], ["DMARC", d.dmarc]].filter(x => !x[1]).map(x => x[0]);
  if (missing.length) return {
    text: `DNS needs a fix (${missing.join(", ")})`,
    tone: "bad"
  };
  return {
    text: "✓ Set up correctly",
    tone: "good"
  };
}
class OutreachInfraComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "load", async () => {
      try {
        const [infra, mbs] = await Promise.all([api("GET", "/infrastructure"), api("GET", "/mailboxes").catch(() => [])]);
        if (this._alive) this.setState({
          infra,
          mbs: Array.isArray(mbs) ? mbs : [],
          error: null
        });
      } catch (e) {
        if (!this._alive) return;
        if (isNotEnabled(e)) this.setState({
          notEnabled: true
        });else this.setState({
          error: errText(e, "Mailboxes could not be loaded."),
          infra: this.state.infra || {
            domains: [],
            mailboxes: [],
            pool: {}
          }
        });
      }
    });
    _defineProperty(this, "act", (key, fn, after) => async () => {
      this.setState({
        busy: key,
        rowErr: {
          ...this.state.rowErr,
          [key]: null
        }
      });
      try {
        const r = await fn();
        if (!this._alive) return;
        this.setState({
          busy: null,
          confirm: null
        });
        if (after) after(r);
        this.load();
      } catch (e) {
        if (this._alive) this.setState({
          busy: null,
          rowErr: {
            ...this.state.rowErr,
            [key]: errText(e, "That didn't work. Try again.")
          }
        });
      }
    });
    _defineProperty(this, "showDns", id => async () => {
      const k = idStr(id);
      if (this.state.dns[k] && !this.state.dns[k].busy) {
        const dns = {
          ...this.state.dns
        };
        delete dns[k];
        this.setState({
          dns
        });
        return;
      }
      this.setState({
        dns: {
          ...this.state.dns,
          [k]: {
            busy: true
          }
        }
      });
      try {
        const v = await api("GET", `/domains/${k}/dns`);
        this.setState({
          dns: {
            ...this.state.dns,
            [k]: {
              checks: v && v.dns || []
            }
          }
        });
      } catch (e) {
        this.setState({
          dns: {
            ...this.state.dns,
            [k]: {
              err: errText(e, "The DNS lookup failed.")
            }
          }
        });
      }
    });
    _defineProperty(this, "recheck", id => this.act(`d-recheck-${idStr(id)}`, () => api("POST", `/domains/${idStr(id)}/recheck`), v => {
      if (v && v.dns) this.setState({
        dns: {
          ...this.state.dns,
          [idStr(id)]: {
            checks: v.dns
          }
        }
      });
    }));
    // ---------------------------------------------------------------- forms
    _defineProperty(this, "submitDomain", async () => {
      const f = this.state.addDomain;
      this.setState({
        addDomain: {
          ...f,
          busy: true,
          err: null
        }
      });
      try {
        const v = await api("POST", "/domains", {
          domain: f.domain.trim().toLowerCase(),
          mailProvider: f.mailProvider,
          trackingHost: f.trackingHost.trim() || undefined,
          registrar: f.registrar.trim() || undefined,
          buy: false
        }, {
          admin: true
        });
        this.setState({
          addDomain: null
        });
        if (v && v.domain && v.dns) this.setState({
          dns: {
            ...this.state.dns,
            [idStr(v.domain.id)]: {
              checks: v.dns
            }
          }
        });
        this.load();
      } catch (e) {
        this.setState({
          addDomain: {
            ...f,
            busy: false,
            err: errText(e, "The domain could not be added.")
          }
        });
      }
    });
    _defineProperty(this, "submitMailbox", async () => {
      const f = this.state.addMailbox;
      this.setState({
        addMailbox: {
          ...f,
          busy: true,
          err: null
        }
      });
      try {
        await api("POST", "/mailboxes", {
          domainId: idStr(f.domainId),
          address: f.address.trim().toLowerCase(),
          displayName: f.displayName.trim() || undefined,
          provider: f.provider || undefined,
          dailyCap: f.dailyCap ? Number(f.dailyCap) : undefined,
          warmupPerDay: f.warmupPerDay ? Number(f.warmupPerDay) : undefined
        }, {
          admin: true
        });
        this.setState({
          addMailbox: null
        });
        this.load();
      } catch (e) {
        this.setState({
          addMailbox: {
            ...f,
            busy: false,
            err: errText(e, "The mailbox could not be added.")
          }
        });
      }
    });
    _defineProperty(this, "submitEdit", async () => {
      const f = this.state.edit;
      this.setState({
        edit: {
          ...f,
          busy: true,
          err: null
        }
      });
      try {
        await api("PUT", `/mailboxes/${f.id}`, {
          displayName: f.displayName,
          dailyCap: f.dailyCap === "" ? undefined : Number(f.dailyCap),
          warmupPerDay: f.warmupPerDay === "" ? undefined : Number(f.warmupPerDay),
          signatureHtml: f.signatureHtml
        });
        this.setState({
          edit: null
        });
        this.load();
      } catch (e) {
        this.setState({
          edit: {
            ...f,
            busy: false,
            err: errText(e, "The mailbox could not be saved.")
          }
        });
      }
    });
    _defineProperty(this, "startConnect", async () => {
      const f = this.state.connect;
      this.setState({
        connect: {
          ...f,
          busy: true,
          err: null
        }
      });
      try {
        const r = await api("POST", `/mailboxes/connect/${f.provider}/start`, {
          domainId: idStr(f.domainId),
          address: f.address.trim() || undefined,
          displayName: f.displayName.trim() || undefined
        });
        this.setState({
          connect: {
            ...f,
            busy: false,
            url: r && r.url
          }
        });
        if (r && r.url) window.open(r.url, "_blank", "noopener");
      } catch (e) {
        this.setState({
          connect: {
            ...f,
            busy: false,
            err: errText(e, "The sign-in could not start.")
          }
        });
      }
    });
    this.state = {
      infra: null,
      mbs: null,
      error: null,
      notEnabled: false,
      busy: null,
      rowErr: {},
      dns: {},
      edit: null,
      confirm: null,
      addDomain: null,
      addMailbox: null,
      connect: null
    };
    this._alive = true;
  }
  async componentDidMount() {
    if (!(Auth.isAuthenticated() || (await Auth.attachOrSignIn()))) return;
    this.load();
  }
  componentWillUnmount() {
    this._alive = false;
  }
  panel(title, sub, onClose, body) {
    return /*#__PURE__*/React.createElement("div", {
      className: "ori-panel"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ori-panel-head"
    }, /*#__PURE__*/React.createElement("b", null, title), /*#__PURE__*/React.createElement("span", {
      className: "ori-grow"
    }), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ori-link",
      onClick: onClose
    }, "Close")), sub ? /*#__PURE__*/React.createElement("span", {
      className: "ori-sub"
    }, sub) : null, body);
  }
  renderDomainForm() {
    const f = this.state.addDomain;
    const set = p => this.setState({
      addDomain: {
        ...f,
        ...p
      }
    });
    return this.panel("Add a domain", "A domain you bought just for outreach and pointed at Microsoft 365 or Google Workspace. Never use your main website domain. We check its DNS right away.", () => this.setState({
      addDomain: null
    }), /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "or-form"
    }, /*#__PURE__*/React.createElement("label", null, /*#__PURE__*/React.createElement("span", {
      style: lbl
    }, "Domain"), /*#__PURE__*/React.createElement("input", {
      style: INPUT,
      value: f.domain,
      placeholder: "yourcompany-mail.com",
      onChange: e => set({
        domain: e.target.value
      })
    })), /*#__PURE__*/React.createElement("label", null, /*#__PURE__*/React.createElement("span", {
      style: lbl
    }, "Mail provider"), /*#__PURE__*/React.createElement(PlunkDropDown, {
      options: [["m365", "Microsoft 365"], ["google", "Google Workspace"]],
      value: f.mailProvider,
      onChange: v => set({
        mailProvider: v
      }),
      width: "100%"
    })), /*#__PURE__*/React.createElement("label", null, /*#__PURE__*/React.createElement("span", {
      style: lbl
    }, "Link tracking address (optional)"), /*#__PURE__*/React.createElement("input", {
      style: INPUT,
      value: f.trackingHost,
      placeholder: "link.yourcompany-mail.com (CNAME to the Plunk tracking address)",
      onChange: e => set({
        trackingHost: e.target.value
      })
    })), /*#__PURE__*/React.createElement("label", null, /*#__PURE__*/React.createElement("span", {
      style: lbl
    }, "Where you bought it (optional)"), /*#__PURE__*/React.createElement("input", {
      style: INPUT,
      value: f.registrar,
      placeholder: "Porkbun, Namecheap\u2026",
      onChange: e => set({
        registrar: e.target.value
      })
    }))), /*#__PURE__*/React.createElement("div", {
      className: "ori-row"
    }, /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: f.busy || !f.domain.trim(),
      onClick: this.submitDomain
    }, f.busy ? "Adding…" : "Add domain"), f.err ? /*#__PURE__*/React.createElement("span", {
      className: "ec-err"
    }, f.err) : /*#__PURE__*/React.createElement("span", {
      className: "ori-sub"
    }, "Avoid endings like .xyz, .top or .click; inboxes distrust them."))));
  }
  renderMailboxForm(domains) {
    const f = this.state.addMailbox;
    const set = p => this.setState({
      addMailbox: {
        ...f,
        ...p
      }
    });
    const dom = domains.find(x => idStr((x.domain || {}).id) === f.domainId);
    return this.panel("Add a mailbox", "A mailbox you already created with your mail provider. It waits until you start warming it up.", () => this.setState({
      addMailbox: null
    }), /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "or-form"
    }, /*#__PURE__*/React.createElement("label", null, /*#__PURE__*/React.createElement("span", {
      style: lbl
    }, "Domain"), /*#__PURE__*/React.createElement(PlunkDropDown, {
      options: [["", "Choose a domain"]].concat(domains.map(x => [idStr(x.domain.id), x.domain.domain])),
      value: f.domainId,
      onChange: v => set({
        domainId: v
      }),
      width: "100%"
    })), /*#__PURE__*/React.createElement("label", null, /*#__PURE__*/React.createElement("span", {
      style: lbl
    }, "Address"), /*#__PURE__*/React.createElement("input", {
      style: INPUT,
      value: f.address,
      placeholder: dom ? `alex@${dom.domain.domain}` : "name@domain",
      onChange: e => set({
        address: e.target.value
      })
    })), /*#__PURE__*/React.createElement("label", null, /*#__PURE__*/React.createElement("span", {
      style: lbl
    }, "Sender name"), /*#__PURE__*/React.createElement("input", {
      style: INPUT,
      value: f.displayName,
      placeholder: "Alex R.",
      onChange: e => set({
        displayName: e.target.value
      })
    })), /*#__PURE__*/React.createElement("label", null, /*#__PURE__*/React.createElement("span", {
      style: lbl
    }, "Provider"), /*#__PURE__*/React.createElement(PlunkDropDown, {
      options: [["", dom && dom.domain.mailProvider ? `Same as the domain (${PROVIDER_LABEL[dom.domain.mailProvider] || dom.domain.mailProvider})` : "Same as the domain"], ["m365", "Microsoft 365"], ["google", "Google Workspace"], ["smtp", "SMTP"]],
      value: f.provider,
      onChange: v => set({
        provider: v
      }),
      width: "100%"
    })), /*#__PURE__*/React.createElement("label", null, /*#__PURE__*/React.createElement("span", {
      style: lbl
    }, "Most new emails a day at full pace"), /*#__PURE__*/React.createElement("input", {
      style: INPUT,
      type: "number",
      min: "1",
      max: "60",
      value: f.dailyCap,
      placeholder: "30",
      onChange: e => set({
        dailyCap: e.target.value
      })
    })), /*#__PURE__*/React.createElement("label", null, /*#__PURE__*/React.createElement("span", {
      style: lbl
    }, "Warm-up emails a day"), /*#__PURE__*/React.createElement("input", {
      style: INPUT,
      type: "number",
      min: "5",
      max: "40",
      value: f.warmupPerDay,
      placeholder: "10",
      onChange: e => set({
        warmupPerDay: e.target.value
      })
    }))), /*#__PURE__*/React.createElement("div", {
      className: "ori-row"
    }, /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: f.busy || !f.domainId || !f.address.trim(),
      onClick: this.submitMailbox
    }, f.busy ? "Adding…" : "Add mailbox"), f.err ? /*#__PURE__*/React.createElement("span", {
      className: "ec-err"
    }, f.err) : /*#__PURE__*/React.createElement("span", {
      className: "ori-sub"
    }, "To send, the mailbox also needs permission from its provider (admin consent for Microsoft 365, sign-in for Google)."))));
  }
  renderConnectForm(domains) {
    const f = this.state.connect;
    const set = p => this.setState({
      connect: {
        ...f,
        ...p
      }
    });
    return this.panel("Connect a mailbox you have", "Sign in with Google or Microsoft. We keep the sign-in in secure storage, never in your data.", () => this.setState({
      connect: null
    }), /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "or-form"
    }, /*#__PURE__*/React.createElement("label", null, /*#__PURE__*/React.createElement("span", {
      style: lbl
    }, "Provider"), /*#__PURE__*/React.createElement(PlunkDropDown, {
      options: [["google", "Google Workspace"], ["microsoft", "Microsoft 365"]],
      value: f.provider,
      onChange: v => set({
        provider: v
      }),
      width: "100%"
    })), /*#__PURE__*/React.createElement("label", null, /*#__PURE__*/React.createElement("span", {
      style: lbl
    }, "Domain"), /*#__PURE__*/React.createElement(PlunkDropDown, {
      options: [["", "Choose a domain"]].concat(domains.map(x => [idStr(x.domain.id), x.domain.domain])),
      value: f.domainId,
      onChange: v => set({
        domainId: v
      }),
      width: "100%"
    })), /*#__PURE__*/React.createElement("label", null, /*#__PURE__*/React.createElement("span", {
      style: lbl
    }, "Address (optional)"), /*#__PURE__*/React.createElement("input", {
      style: INPUT,
      value: f.address,
      onChange: e => set({
        address: e.target.value
      })
    })), /*#__PURE__*/React.createElement("label", null, /*#__PURE__*/React.createElement("span", {
      style: lbl
    }, "Sender name (optional)"), /*#__PURE__*/React.createElement("input", {
      style: INPUT,
      value: f.displayName,
      onChange: e => set({
        displayName: e.target.value
      })
    }))), /*#__PURE__*/React.createElement("div", {
      className: "ori-row"
    }, /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: f.busy || !f.domainId,
      onClick: this.startConnect
    }, f.busy ? "Starting…" : "Continue to sign in"), f.err ? /*#__PURE__*/React.createElement("span", {
      className: "ec-err"
    }, f.err) : f.url ? /*#__PURE__*/React.createElement("span", {
      className: "ori-sub"
    }, "The sign-in opened in a new tab. ", /*#__PURE__*/React.createElement("a", {
      href: f.url,
      target: "_blank",
      rel: "noopener noreferrer"
    }, "Open it again"), "; reload this page when it finishes.") : null)));
  }
  renderEdit(health) {
    const {
      edit
    } = this.state;
    const set = p => this.setState({
      edit: {
        ...edit,
        ...p
      }
    });
    return this.panel(`Edit ${(health.find(h => idStr(h.mailboxId) === edit.id) || {}).address || "mailbox"}`, null, () => this.setState({
      edit: null
    }), /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "or-form"
    }, /*#__PURE__*/React.createElement("label", null, /*#__PURE__*/React.createElement("span", {
      style: lbl
    }, "Sender name"), /*#__PURE__*/React.createElement("input", {
      style: INPUT,
      value: edit.displayName,
      onChange: e => set({
        displayName: e.target.value
      })
    })), /*#__PURE__*/React.createElement("label", null, /*#__PURE__*/React.createElement("span", {
      style: lbl
    }, "Most new emails a day at full pace (1 to 60)"), /*#__PURE__*/React.createElement("input", {
      style: INPUT,
      type: "number",
      min: "1",
      max: "60",
      value: edit.dailyCap,
      onChange: e => set({
        dailyCap: e.target.value
      })
    })), /*#__PURE__*/React.createElement("label", null, /*#__PURE__*/React.createElement("span", {
      style: lbl
    }, "Warm-up emails a day (5 to 40)"), /*#__PURE__*/React.createElement("input", {
      style: INPUT,
      type: "number",
      min: "5",
      max: "40",
      value: edit.warmupPerDay,
      onChange: e => set({
        warmupPerDay: e.target.value
      })
    })), /*#__PURE__*/React.createElement("label", {
      className: "wide"
    }, /*#__PURE__*/React.createElement("span", {
      style: lbl
    }, "Signature (HTML)"), /*#__PURE__*/React.createElement("textarea", {
      style: {
        ...INPUT,
        minHeight: 60,
        fontFamily: "inherit"
      },
      value: edit.signatureHtml,
      onChange: e => set({
        signatureHtml: e.target.value
      })
    }))), /*#__PURE__*/React.createElement("div", {
      className: "ori-row"
    }, /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: edit.busy,
      onClick: this.submitEdit
    }, edit.busy ? "Saving…" : "Save"), /*#__PURE__*/React.createElement(Button, {
      disabled: edit.busy,
      onClick: () => this.setState({
        edit: null
      })
    }, "Cancel"), edit.err ? /*#__PURE__*/React.createElement("span", {
      className: "ec-err"
    }, edit.err) : null)));
  }

  // ---------------------------------------------------------------- cards

  mailboxMenu(h, m) {
    const id = idStr(h.mailboxId);
    const k = a => `m-${a}-${id}`;
    const st = h.status;
    const items = [];
    if (st === "provisioning") items.push({
      label: "Start warming up",
      onClick: this.act(k("resume"), () => api("POST", `/mailboxes/${id}/resume`))
    });
    if (st === "warming" || st === "active") items.push({
      label: "Pause",
      onClick: this.act(k("pause"), () => api("POST", `/mailboxes/${id}/pause`, {
        reason: "manual"
      }))
    });
    if (st === "paused") items.push({
      label: "Resume",
      onClick: this.act(k("resume"), () => api("POST", `/mailboxes/${id}/resume`))
    });
    if (st !== "disabled") items.push({
      label: "Edit",
      onClick: () => this.setState({
        edit: {
          id,
          displayName: m && m.displayName || "",
          dailyCap: String(h.dailyCap || ""),
          warmupPerDay: String(h.warmupPerDay || ""),
          signatureHtml: m && m.signatureHtml || ""
        }
      })
    });
    if (st !== "disabled" && isAdmin()) items.push({
      label: "Turn off",
      danger: true,
      onClick: () => this.setState({
        confirm: k("disable")
      })
    });
    return /*#__PURE__*/React.createElement(Menu, {
      label: `Actions for ${h.address}`,
      disabled: !!this.state.busy,
      items: items
    });
  }
  renderMailbox(h, byId) {
    const {
      busy,
      confirm,
      rowErr
    } = this.state;
    const id = idStr(h.mailboxId);
    const m = byId[id];
    const k = a => `m-${a}-${id}`;
    const state = mailboxState(h);
    const err = ["resume", "pause", "disable"].map(a => rowErr[k(a)]).find(Boolean);
    const notes = (h.findings || []).map(plainFinding).filter(Boolean);
    const working = ["resume", "pause", "disable"].some(a => busy === k(a));
    return /*#__PURE__*/React.createElement("div", {
      key: id,
      className: "ori-mb"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ori-mb-row"
    }, /*#__PURE__*/React.createElement("span", {
      className: "ori-mb-addr",
      title: m && m.displayName ? `${m.displayName} · ${PROVIDER_LABEL[m.provider] || m.provider || ""}` : undefined
    }, h.address, m && !m.hasToken && h.status !== "disabled" ? /*#__PURE__*/React.createElement("span", {
      className: "ori-mb-flag"
    }, "not connected yet") : null), /*#__PURE__*/React.createElement("span", {
      className: `ori-state ${state.tone}`
    }, working ? "Saving…" : state.text), /*#__PURE__*/React.createElement("span", {
      className: "ori-mb-today",
      title: `Can send ${num(h.capToday)} new emails today (${num(h.sentToday)} sent so far). Full pace is ${num(h.dailyCap)} a day.`
    }, num(h.capToday), " of ", num(h.dailyCap), " today"), this.mailboxMenu(h, m)), notes.length ? /*#__PURE__*/React.createElement("div", {
      className: "ori-mb-notes"
    }, notes.join(" ")) : null, confirm === k("disable") ? /*#__PURE__*/React.createElement("div", {
      className: "ori-confirm"
    }, /*#__PURE__*/React.createElement("span", null, "Turn off ", h.address, "? It stops sending and its sign-in is deleted."), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: !!busy,
      onClick: this.act(k("disable"), () => api("POST", `/mailboxes/${id}/disable`, {}, {
        admin: true
      }))
    }, busy === k("disable") ? "Turning off…" : "Yes, turn off"), /*#__PURE__*/React.createElement(Button, {
      onClick: () => this.setState({
        confirm: null
      })
    }, "Keep it")) : null, err ? /*#__PURE__*/React.createElement("div", {
      className: "ec-err"
    }, err) : null);
  }
  renderDomain(dv, rows, byId) {
    const {
      busy,
      confirm,
      rowErr,
      dns
    } = this.state;
    const d = dv.domain || {};
    const id = idStr(d.id);
    const k = a => `d-${a}-${id}`;
    const rec = dns[id];
    const state = domainState(d);
    const items = [{
      label: rec ? "Hide DNS records" : "DNS records",
      onClick: this.showDns(d.id)
    }, {
      label: "Check again",
      onClick: this.recheck(d.id)
    }, d.status === "paused" ? {
      label: "Resume",
      onClick: this.act(k("resume"), () => api("POST", `/domains/${id}/resume`))
    } : d.status !== "retired" ? {
      label: "Pause",
      onClick: this.act(k("pause"), () => api("POST", `/domains/${id}/pause`))
    } : null, d.status !== "retired" && isAdmin() ? {
      label: "Retire",
      danger: true,
      onClick: () => this.setState({
        confirm: k("retire")
      })
    } : null];
    const err = ["recheck", "pause", "resume", "retire"].map(a => rowErr[k(a)]).find(Boolean);
    const working = ["recheck", "pause", "resume", "retire"].some(a => busy === k(a));
    return /*#__PURE__*/React.createElement("div", {
      key: id,
      className: "ori-dom"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ori-dom-head"
    }, /*#__PURE__*/React.createElement("b", null, d.domain), /*#__PURE__*/React.createElement("span", {
      className: `ori-state ${state.tone}`
    }, working ? busy === k("recheck") ? "Checking…" : "Saving…" : state.text), /*#__PURE__*/React.createElement(Menu, {
      label: `Actions for ${d.domain}`,
      disabled: !!busy,
      items: items
    })), confirm === k("retire") ? /*#__PURE__*/React.createElement("div", {
      className: "ori-confirm"
    }, /*#__PURE__*/React.createElement("span", null, "Retire ", d.domain, "? Its mailboxes are turned off."), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: !!busy,
      onClick: this.act(k("retire"), () => api("POST", `/domains/${id}/retire`, {}, {
        admin: true
      }))
    }, busy === k("retire") ? "Retiring…" : "Yes, retire"), /*#__PURE__*/React.createElement(Button, {
      onClick: () => this.setState({
        confirm: null
      })
    }, "Keep it")) : null, err ? /*#__PURE__*/React.createElement("div", {
      className: "ec-err"
    }, err) : null, rec ? /*#__PURE__*/React.createElement("div", {
      className: "ori-dns"
    }, rec.busy ? /*#__PURE__*/React.createElement("span", {
      className: "ori-sub"
    }, "Looking up the records\u2026") : rec.err ? /*#__PURE__*/React.createElement("span", {
      className: "ec-err"
    }, rec.err) : /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Record"), /*#__PURE__*/React.createElement("th", null, "State"), /*#__PURE__*/React.createElement("th", null, "Should be"), /*#__PURE__*/React.createElement("th", null, "Found"))), /*#__PURE__*/React.createElement("tbody", null, (rec.checks || []).map(c => /*#__PURE__*/React.createElement("tr", {
      key: c.record
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("b", null, c.record)), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
      className: `ori-state ${c.ok ? "good" : "bad"}`
    }, c.ok ? "✓ Correct" : "Missing or wrong"), c.note ? /*#__PURE__*/React.createElement("div", {
      className: "ori-sub"
    }, c.note) : null), /*#__PURE__*/React.createElement("td", {
      className: "mono"
    }, c.expected || "-"), /*#__PURE__*/React.createElement("td", {
      className: "mono"
    }, c.found || "-"))))))) : null, /*#__PURE__*/React.createElement("div", {
      className: "ori-mbs"
    }, rows.length ? rows.map(h => this.renderMailbox(h, byId)) : /*#__PURE__*/React.createElement("div", {
      className: "ori-sub ori-mb-empty"
    }, "No mailboxes on this domain yet.")));
  }
  render() {
    if (!hasTenantFeature("outreach") || this.state.notEnabled) return /*#__PURE__*/React.createElement(NotEnabled, {
      title: "Outreach mailboxes"
    });
    const {
      infra,
      mbs,
      error,
      edit,
      addDomain,
      addMailbox,
      connect
    } = this.state;
    const loading = infra === null;
    const domains = infra && infra.domains || [];
    const health = infra && infra.mailboxes || [];
    const pool = infra && infra.pool || {};
    const byId = {};
    (mbs || []).forEach(m => {
      byId[idStr(m.id)] = m;
    });
    const admin = isAdmin();

    // group mailbox rows under their domain (GET mailboxes carries domainId; fall back to the address's domain)
    const groups = {};
    domains.forEach(dv => {
      groups[idStr((dv.domain || {}).id)] = [];
    });
    const orphans = [];
    health.forEach(h => {
      const m = byId[idStr(h.mailboxId)];
      let key = m ? idStr(m.domainId) : null;
      if (!key || !groups[key]) {
        const host = String(h.address || "").split("@")[1] || "";
        const dv = domains.find(x => (x.domain || {}).domain === host);
        key = dv ? idStr(dv.domain.id) : null;
      }
      if (key && groups[key]) groups[key].push(h);else orphans.push(h);
    });
    const live = health.filter(h => h.status !== "disabled");
    const cnt = s => live.filter(h => h.status === s).length;
    const warming = live.filter(h => h.status === "warming");
    const stateWords = (() => {
      if (!live.length) return health.length ? "all turned off" : "none yet";
      const parts = [["warming", "warming up"], ["active", "ready"], ["paused", "paused"], ["provisioning", "not started"]].map(([s, w]) => [cnt(s), w]).filter(x => x[0]);
      if (parts.length === 1) return `all ${parts[0][1]}`;
      return parts.map(([n, w]) => `${num(n)} ${w}`).join(", ");
    })();
    const sum = f => live.reduce((a, h) => a + (Number(h[f]) || 0), 0);
    const sent7 = sum("sent7d");
    const bouncePct = sent7 ? sum("bounces7d") / sent7 * 100 : 0;
    const minDay = warming.length ? Math.min(...warming.map(h => Number(h.warmDay) || 0)) : null;
    const fullPace = minDay != null ? moment().add(Math.max(0, WARMUP_DAYS + 1 - minDay), "days").format("MMM D") : null;
    const swapLine = `${num(pool.exchanges7d)} swapped in the last 7 days, ${num(pool.replied7d)} replied to.`;
    let headline, explain;
    if (warming.length) {
      headline = `Warming up: day ${num(Math.max(1, minDay))} of ${WARMUP_DAYS}`;
      explain = `Each mailbox swaps friendly emails with other mailboxes so inboxes learn to trust it. ${swapLine} Full pace around ${fullPace}.`;
    } else if (cnt("active")) {
      headline = "Ready";
      explain = `Your mailboxes keep swapping friendly emails with other mailboxes so inboxes keep trusting them. ${swapLine}`;
    } else if (cnt("provisioning")) {
      headline = "Not started yet";
      explain = `Start warming up a mailbox from its ··· menu. For about three weeks it swaps friendly emails with other mailboxes so inboxes learn to trust it, then it sends at full pace.`;
    } else if (cnt("paused")) {
      headline = "Paused";
      explain = "Every mailbox is paused. Resume one from its ··· menu.";
    } else {
      headline = "No mailboxes yet";
      explain = "Add a domain you bought for outreach, then its mailboxes. Each mailbox warms up for about three weeks before it sends at full pace.";
    }
    return /*#__PURE__*/React.createElement("div", {
      className: "ori-page"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ori-top"
    }, /*#__PURE__*/React.createElement(Link, {
      to: "/Email/outreach",
      className: "ori-back",
      "aria-label": "Back to Outreach",
      title: "Back to Outreach"
    }, /*#__PURE__*/React.createElement("svg", {
      width: "16",
      height: "16",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M15 18l-6-6 6-6"
    }))), /*#__PURE__*/React.createElement("div", {
      className: "ori-title"
    }, /*#__PURE__*/React.createElement("span", {
      className: "t"
    }, "Outreach mailboxes"), /*#__PURE__*/React.createElement("span", {
      className: "s"
    }, loading ? "Loading…" : `${num(health.length)} mailbox${health.length === 1 ? "" : "es"} on ${num(domains.length)} domain${domains.length === 1 ? "" : "s"} · ${stateWords}`)), /*#__PURE__*/React.createElement("span", {
      className: "ori-grow"
    }), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ori-btn ori-btn-lg",
      disabled: !domains.length,
      title: domains.length ? undefined : "Add a domain first",
      onClick: () => this.setState({
        connect: {
          provider: "google",
          domainId: "",
          address: "",
          displayName: ""
        }
      })
    }, "Connect a mailbox you have"), admin ? /*#__PURE__*/React.createElement(Menu, {
      label: "Add mailboxes",
      trigger: "Add mailboxes",
      triggerCls: "ori-btn ori-btn-lg ori-primary",
      items: [{
        label: "Add a mailbox",
        disabled: !domains.length,
        onClick: () => this.setState({
          addMailbox: {
            domainId: "",
            address: "",
            displayName: "",
            provider: "",
            dailyCap: "",
            warmupPerDay: ""
          }
        })
      }, {
        label: "Add a domain",
        onClick: () => this.setState({
          addDomain: {
            domain: "",
            mailProvider: "m365",
            trackingHost: "",
            registrar: ""
          }
        })
      }]
    }) : null), /*#__PURE__*/React.createElement("div", {
      className: "ori-body-pad"
    }, error ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice danger"
    }, error) : null, addDomain ? this.renderDomainForm() : null, addMailbox ? this.renderMailboxForm(domains) : null, connect ? this.renderConnectForm(domains) : null, edit ? this.renderEdit(health) : null, /*#__PURE__*/React.createElement("div", {
      className: "ori-summary"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ori-summary-text"
    }, /*#__PURE__*/React.createElement("b", null, loading ? "Loading…" : headline), loading ? null : /*#__PURE__*/React.createElement("span", null, explain)), /*#__PURE__*/React.createElement("div", {
      className: "ori-figs"
    }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, loading ? "-" : `${num(sum("capToday"))} of ${num(sum("dailyCap"))}`), "can send today"), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, loading ? "-" : num(sent7)), "sent this week"), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, loading ? "-" : `${bouncePct < 10 && bouncePct > 0 ? bouncePct.toFixed(1) : Math.round(bouncePct)}%`), "bounced"))), loading ? /*#__PURE__*/React.createElement("div", {
      className: "ori-blank"
    }, "Loading\u2026") : !domains.length ? /*#__PURE__*/React.createElement("div", {
      className: "ori-blank"
    }, "No domains yet. Add a domain you bought just for outreach (never your main website domain), then add its mailboxes.") : /*#__PURE__*/React.createElement("div", {
      className: "ori-doms"
    }, domains.map(dv => this.renderDomain(dv, groups[idStr((dv.domain || {}).id)] || [], byId)), orphans.length ? /*#__PURE__*/React.createElement("div", {
      className: "ori-dom"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ori-dom-head"
    }, /*#__PURE__*/React.createElement("b", null, "Other mailboxes")), /*#__PURE__*/React.createElement("div", {
      className: "ori-mbs"
    }, orphans.map(h => this.renderMailbox(h, byId)))) : null), /*#__PURE__*/React.createElement("span", {
      className: "ori-foot"
    }, "A mailbox pauses itself if ", BOUNCE_PAUSE_PCT, "% or more of its emails bounce in a week. Each domain's \xB7\xB7\xB7 menu has its DNS records, Check again, Pause and Retire.")));
  }
}
export default withRouter(OutreachInfraComponent);